import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { umaPrograms, UMA_TCPA_DISCLOSURE } from '../data/umaPrograms';
import { CLINICAL_PROGRAM_SLUG, isClinicalZip } from '../data/umaClinicalZips';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { LegalLinks } from '../components/LegalLinks';
import { tcpaSearch, tcpaPostDetailed, leadsSearch, clicksSearch } from '../services/mediaMatchers';
import { trackLeadConversion } from '../utils/conversion';
import type { ContactProfile, SearchProfile, TCPAResult } from '../services/mediaMatchers';
import { sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import { DEFAULT_TCPA_DISCLOSURE } from '../data/tcpaDisclosure';

/**
 * The brand that, on a valid TCPA post, ends the flow at the thank-you page
 * instead of continuing into the leads and clicks carousel.
 */
const PRIORITY_BRAND_ID = '11154';
/**
 * UMA's own codes, posted verbatim to UMA's webhook.
 *
 * `matchLabel` is the equivalent wording in the Media Matchers spec. The match
 * flow sends that instead, because the shared payload builder maps from labels
 * and would otherwise fall through to the "other" code for every answer.
 */
const umaEducationLevels = [{
  label: 'High School Diploma',
  value: '1',
  matchLabel: 'High School Diploma / GED'
}, {
  label: 'GED',
  value: '3',
  matchLabel: 'High School Diploma / GED'
}, {
  label: '0-12 College Credits',
  value: '4',
  matchLabel: 'Some College'
}, {
  label: 'More Than 12 College Credits',
  value: '5',
  matchLabel: 'Some College'
}, {
  label: 'Associates Degree',
  value: '6',
  matchLabel: "Associate's Degree"
}, {
  label: 'Bachelors Degree',
  value: '7',
  matchLabel: "Bachelor's Degree"
}, {
  label: 'Graduate Degree',
  value: '8',
  matchLabel: "Master's Degree"
}];
const umaStartTimeframes = [{
  label: 'Immediately',
  value: '1',
  matchLabel: 'Immediately'
}, {
  label: '1-3 Months',
  value: '2',
  matchLabel: '1-3 months'
}, {
  label: '4-6 Months',
  value: '3',
  matchLabel: '3-6 months'
}, {
  label: '7-12 Months',
  value: '4',
  matchLabel: '6-12 months'
}, {
  label: 'Later Than 1 Year',
  value: '5',
  matchLabel: 'More than a year'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
type Panel = 'zip' | 'program' | 'background' | 'location' | 'contact';
/**
 * Resolves a program from the URL so a campaign link can skip the picker step.
 * Accepts ?program= (slug or route slug) or ?mProgram= (webhook code).
 */
function getProgramSlugFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const requested = (params.get('program') || params.get('mProgram') || '').trim();
  if (!requested) return '';
  const match = umaPrograms.find(p => p.slug.toLowerCase() === requested.toLowerCase() || p.routeSlug.toLowerCase() === requested.toLowerCase() || p.mProgram.toLowerCase() === requested.toLowerCase());
  return match?.slug || '';
}
interface UMALandingPageProps {
  /**
   * Asks for the ZIP before the program picker. Used by
   * /find-programs/ultimate-medical-academy, where the visitor arrives without
   * a program in mind and location is the lowest-commitment opening question.
   */
  zipFirst?: boolean;
  /** Where the thank-you page sends the visitor back to. */
  backUrl?: string;
  /**
   * Renders the page under Ultimate Medical Academy's own branding — logo bar,
   * school footer, and the claims, testimonial, and accreditation notice that
   * are specific to UMA. Turn this off for a neutral program search that sits
   * under the site's own nav.
   */
  branded?: boolean;
  /**
   * Submits through the Media Matchers match flow — TCPA Search after the
   * location step, then TCPA Post plus Leads and Clicks Search on submit,
   * handing off to the /matches carousel. Off by default, where the lead goes
   * straight to UMA's own webhook and thank-you page.
   */
  matchFlow?: boolean;
  /**
   * UMA course code posted when, against expectation, the visitor reaches
   * submit with no program selected. Without it `mProgram` goes out empty and
   * the lead lands unattributed to a program.
   */
  fallbackMProgram?: string;
}
export function UMALandingPage({
  zipFirst = false,
  backUrl = '/colleges/ultimate-medical-academy',
  branded = true,
  matchFlow = false,
  fallbackMProgram = ''
}: UMALandingPageProps = {}) {
  usePageMeta(branded ? 'Ultimate Medical Academy Online | Healthcare Career Training Programs' : 'Online Medical Program Search | Online College Guide', branded ? 'Explore online healthcare degree programs at Ultimate Medical Academy. Get personalized support, financial aid options, and job search assistance. Request info today.' : 'Search accredited online medical and healthcare programs. Answer a few questions and get matched by career goals, schedule, and location.', {
    ogImage: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [preselectedProgram] = useState(getProgramSlugFromUrl);
  const [selectedProgram, setSelectedProgram] = useState(preselectedProgram);
  // The funnel is ordered rather than hard-coded so the /find-programs variant
  // can open on location without a second copy of the form.
  const panels = React.useMemo<Panel[]>(() => {
    const base: Panel[] = zipFirst ? ['zip', 'program', 'background', 'location', 'contact'] : ['program', 'background', 'location', 'contact'];
    return preselectedProgram ? base.filter(p => p !== 'program') : base;
  }, [zipFirst, preselectedProgram]);
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  // Media Matchers match-flow state, used only when `matchFlow` is on.
  const [tcpa, setTcpa] = useState<TCPAResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tcpaFailed, setTcpaFailed] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const searchFired = useRef(false);
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  // Fetch user IP on mount
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const currentPanel = panels[formStep];
  const [programError, setProgramError] = useState('');
  /**
   * Clinical Medical Assistant requires an in-person externship, so it is only
   * offered inside its placement area. Once a ZIP is known, an out-of-area
   * visitor never sees a program they cannot enroll in.
   */
  const availablePrograms = React.useMemo(() => {
    const zip = formData.zipCode.replace(/\D/g, '');
    if (zip.length < 5 || isClinicalZip(zip)) return umaPrograms;
    return umaPrograms.filter(p => p.slug !== CLINICAL_PROGRAM_SLUG);
  }, [formData.zipCode]);
  // A ZIP entered after the fact can invalidate an earlier selection.
  React.useEffect(() => {
    if (selectedProgram && !availablePrograms.some(p => p.slug === selectedProgram)) {
      setSelectedProgram('');
    }
  }, [availablePrograms, selectedProgram]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    setSelectedProgram(newSlug);
    if (newSlug) setProgramError('');
    // Picking a program answers the whole panel, so advance rather than
    // making the visitor confirm a choice they just made.
    if (newSlug && currentPanel === 'program') {
      setFormStep(formStep + 1);
    }
  };
  const buildProfile = useCallback((): SearchProfile => {
    const context = umaPrograms.find(p => p.slug === selectedProgram)?.match;
    return {
      degreeInterest: context?.degreeInterest || '',
      areaStudy: context?.areaStudy || '',
      mProgram: context?.mProgram || '',
      // Translated out of UMA's codes into the partner's wording, so the
      // shared payload builder produces the correct spec codes.
      currentEducation: umaEducationLevels.find(o => o.value === formData.currentEducation)?.matchLabel || '',
      gradYear: formData.gradYear,
      startTimeframe: umaStartTimeframes.find(o => o.value === formData.startDate)?.matchLabel || '',
      militaryAffiliation: formData.isMilitary,
      zipCode: formData.zipCode.replace(/\D/g, ''),
      address: formData.address,
      city: formData.city,
      state: formData.state
    };
  }, [selectedProgram, formData]);
  /**
   * TCPA Search runs once the contact step is done, so the phone and email are
   * part of the match and the partner's own consent language is resolved before
   * the visitor is asked to tick the box.
   */
  const runTcpaSearch = useCallback(async () => {
    setSearchLoading(true);
    setTcpaFailed(false);
    const result = await tcpaSearch(buildProfile());
    setTcpa(result);
    if (!result) setTcpaFailed(true);
    setSearchLoading(false);
    return result;
  }, [buildProfile]);
  /**
   * Every field the TCPA Search depends on is filled in, so the search can run
   * without the visitor having to press anything. Checked without setting
   * errors — this drives a background request, not a validation message.
   */
  const contactComplete = formData.firstName.trim().length > 0 && formData.lastName.trim().length > 0 && /^\S+@\S+\.\S+$/.test(formData.email.trim()) && stripPhone(formData.phone).length >= 10;
  // Fires once the last required field lands, so the partner's disclosure
  // replaces the placeholder in situ rather than gating a step.
  React.useEffect(() => {
    if (!matchFlow || searchFired.current) return;
    if (currentPanel !== 'contact' || !contactComplete) return;
    searchFired.current = true;
    runTcpaSearch();
  }, [matchFlow, currentPanel, contactComplete, runTcpaSearch]);
  const goBack = () => setFormStep(Math.max(0, formStep - 1));
  const handleNext = () => {
    if (currentPanel === 'zip') {
      if (!validateUniStep2()) return;
      // Resolved here so the later location step opens already filled in.
      lookupZip(formData.zipCode);
    } else if (currentPanel === 'program') {
      if (!selectedProgram) {
        setProgramError('Please choose a program to continue.');
        return;
      }
    } else if (currentPanel === 'background') {
      if (!validateUniStep1()) return;
    } else if (currentPanel === 'location') {
      if (!validateUniStep2()) return;
      // The address fields only appear once the ZIP resolves, so reveal them
      // rather than erroring on a field the visitor cannot see yet.
      if (!zipLookedUp) {
        lookupZip(formData.zipCode);
        return;
      }
      const addrValid = formData.address.trim().length > 0;
      setAddressError(addrValid ? '' : 'Street address is required.');
      if (!addrValid) return;
    }
    setFormStep(formStep + 1);
  };
  /**
   * Media Matchers submit: consent is posted first, then Leads and Clicks
   * Search run together with the full profile, excluding the brand that just
   * took consent. Mirrors the /program-information hero form.
   */
  const handleMatchSubmit = async () => {
    setSubmitError('');
    if (!validateUniStep3()) return;
    if (!tcpa) {
      setTcpaFailed(true);
      setSubmitError('We could not load the consent details. Please retry before submitting.');
      return;
    }
    setIsSubmitting(true);
    const profile = buildProfile();
    const contact: ContactProfile = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: stripPhone(formData.phone)
    };
    const posted = await tcpaPostDetailed(tcpa.mRecordID, contact);
    // When the priority brand takes the lead and confirms it as valid, the
    // visitor is done — showing more schools after a completed handoff only
    // adds friction. `leadValid === null` means the response was unreadable,
    // which is NOT an acceptance, so the normal flow continues.
    if (tcpa.mBrandID === PRIORITY_BRAND_ID && posted.leadValid === true) {
      trackLeadConversion({
        source: 'tcpa',
        brand: tcpa.mBrandID
      });
      setIsSubmitting(false);
      // No `partner` here: this flow is unbranded, and the thank-you page's
      // partner layout needs a name, logo, and colors we do not have for a
      // brand id alone. The generic confirmation is the correct surface.
      navigate('/thank-you', {
        state: {
          formData
        }
      });
      return;
    }
    const [matchedLeads, clicks] = await Promise.all([leadsSearch(profile, contact, tcpa.mBrandID), clicksSearch(profile, contact, tcpa.mBrandID)]);
    setIsSubmitting(false);
    navigate('/matches', {
      state: {
        leads: matchedLeads || [],
        clicks: clicks || [],
        profile,
        contact,
        disclosure: tcpa.disclosure,
        excludeBrand: tcpa.mBrandID
      }
    });
  };
  const handleStep3Submit = async () => {
    if (matchFlow) return handleMatchSubmit();
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = umaPrograms.find(p => p.slug === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || fallbackMProgram,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/u7uetpg/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Ultimate Medical Academy',
            logo: 'https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp',
            primaryColor: '#003B5C',
            accentColor: '#00BFA5',
            backUrl,
            programTitle: currentProgram?.title || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = panels.length;
  const progressPercent = Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`;
  const currentProgramTitle = umaPrograms.find(p => p.slug === selectedProgram)?.title;
  const renderFormContent = () => {
    if (currentPanel === 'zip') {
      return <div className="space-y-4" data-id="element-3303" data-mp="1jf7amvzw9oir">
          <div data-id="element-3304" data-mp="1ndok3m6sfb3c">
            <label htmlFor="startZip" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3305" data-mp="1nd51s4d86kos">
              Zip Code
            </label>
            <input type="text" id="startZip" inputMode="numeric" autoComplete="postal-code" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-3306" data-mp="1xcdrum4yzf5v" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-3307" data-mp="1zwiv5xlulw8t">{errors.zipCode}</p>}
          </div>
          <p className="text-xs text-gray-500" data-id="element-3308" data-mp="2ekn3q8r6srdv">
            We use your zip code to show the programs available in your area.
          </p>
          <button type="button" onClick={handleNext} className="w-full bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-3309" data-mp="29p7slokkd9l5">
            Next
          </button>
        </div>;
    }
    if (currentPanel === 'program') {
      return <div className="space-y-4" data-id="element-3310" data-mp="1qw46co9odz0k">
          <div data-id="element-3311" data-mp="1qv2itkbh3k1y">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3312" data-mp="1qygk0p8ehn7t">
              Select a Program
            </label>
            <div className="relative" data-id="element-3313" data-mp="32b9bdyxa49nc">
              <select id="hero-program-select" value={selectedProgram} onChange={handleProgramChange} className={`block w-full px-4 py-3 text-sm border rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-800 ${programError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`} data-id="element-3314" data-mp="39vbki96btxl1">
                <option value="" data-id="element-3315" data-mp="34x08pswh1d06">-- Choose a Program --</option>
                {availablePrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-3316" data-mp="3smku0ig3kmcm">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-3317" data-mp="1egptgzonji1">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3318" data-mp="3jrevm4j5p6k4" />
              </div>
            </div>
            {programError && <p className="mt-1 text-xs text-red-600" data-id="element-3319" data-mp="ff19sghc3ld4">{programError}</p>}
          </div>
          {formStep === 0 ? <p className="text-xs text-gray-500 text-center" data-id="element-3320" data-mp="k5q4e4ob8ygh">
              Select a program above to get started
            </p> : <div className="flex gap-3 pt-2" data-id="element-3321" data-mp="2bqasttqh3ljf">
              <button type="button" onClick={goBack} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3322" data-mp="2brm2ga6lnnig">
                <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3323" data-mp="27044luzvi31p" />
                Back
              </button>
              <button type="button" onClick={handleNext} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-3324" data-mp="1t0cj79711kdo">
                Next
              </button>
            </div>}
        </div>;
    }
    if (currentPanel === 'background') {
      return <div className="space-y-4" data-id="element-3325" data-mp="1o1hyu2tevnrg">
          <div data-id="element-3326" data-mp="3dgfvdb0d8in9">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3327" data-mp="3dfy1um538ij1">
              Program
            </label>
            <div className="relative" data-id="element-3328" data-mp="16rxbdu7ibzhs">
              <select id="step1-program" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-3329" data-mp="16sz0v3p9soj1">
                {availablePrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-3330" data-mp="1bra8d7s3emy3">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-3331" data-mp="1phdmfbgtjacg">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3332" data-mp="1lga8iuibzodv" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-3333" data-mp="1zfth1of211qp" />

          <div data-id="element-3334" data-mp="1uh8hlr7mmm3b">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3335" data-mp="1uita7badruyj">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-3336" data-mp="4a5d22d9fdc">
              <option value="" data-id="element-3337" data-mp="254jo1mp6wtgy">-- Please Select --</option>
              {umaStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-3338" data-mp="296eo6nvdx0t5">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-3339" data-mp="2e6as6n430s8p">{errors.startDate}</p>}
          </div>

          <div data-id="element-3340" data-mp="2j3t48mdebnkx">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3341" data-mp="2j42so5islmoi">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-3342" data-mp="2svp1xwbklbom">
              <option value="" data-id="element-3343" data-mp="31wetgntssfwj">-- Please Select --</option>
              {umaEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-3344" data-mp="36v87chczy8x8">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-3345" data-mp="3bzaio2o32y7r">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-3346" data-mp="370pe5r1stsdi">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3347" data-mp="370fvypmzwpid">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-3348" data-mp="3hw3pn4bhjnk">
              <option value="" data-id="element-3349" data-mp="3pyjwwwsqbi93">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-3350" data-mp="3lx6n6bmimuww">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-3351" data-mp="m9yrrjjag27u">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-3352" data-mp="hiqeudir1ypx">
            {formStep > 0 && <button type="button" onClick={goBack} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3353" data-mp="hh5kanwfqfs2">
                <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3354" data-mp="3p5yb4d6jrhq9" />
                Back
              </button>}
            <button type="button" onClick={handleNext} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-3355" data-mp="3t6qhmyo1b8iu">
              Next
            </button>
          </div>
        </div>;
    }
    if (currentPanel === 'location') {
      return <div className="space-y-4" data-id="element-3356" data-mp="bx25q3idvkzc">
          <div data-id="element-3357" data-mp="g089n4i0x02n">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3358" data-mp="fzorbmogo9o3">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-3359" data-mp="18h13knv5nqr7" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-3360" data-mp="2xy6fob8c0lqq">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-3361" data-mp="2y0iuwgmpgi03">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-3362" data-mp="2a93x915viov6" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-3363" data-mp="2k7tauhqqln5m">
                <div data-id="element-3364" data-mp="2f7oqggmpk8a4">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3365" data-mp="2f7fallvdnhi5">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#4ECDC4] focus:border-[#4ECDC4] bg-gray-50" placeholder="City" data-id="element-3366" data-mp="3qmu7kpnl69d9" />
                </div>
                <div data-id="element-3367" data-mp="bj3ingyxr1ix">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3368" data-mp="bikc0weqjzk7">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#4ECDC4] focus:border-[#4ECDC4] bg-gray-50" placeholder="State" data-id="element-3369" data-mp="3ezsqd6a98ha6" />
                </div>
              </div>
              <div data-id="element-3370" data-mp="3tpi5k5f6mltg">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3371" data-mp="3tpzxiputadvk">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`} placeholder="123 Main St" data-id="element-3372" data-mp="31746k2i40tss" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-3373" data-mp="14d07lm261jer">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-3374" data-mp="zg9sp223ktux">
            {formStep > 0 && <button type="button" onClick={goBack} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3375" data-mp="umng19vxopz4">
                <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3376" data-mp="lolk38r24nip" />
                Back
              </button>}
            <button type="button" onClick={handleNext} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-3377" data-mp="gmwazyneajx9">
              Next
            </button>
          </div>
        </div>;
    }
    if (currentPanel === 'contact') {
      return <div className="space-y-4" data-id="element-3378" data-mp="1qjj7prez7fsj">
          <div className="grid grid-cols-2 gap-3" data-id="element-3379" data-mp="1qkcegsdxq5ao">
            <div data-id="element-3380" data-mp="1viw699f7mbay">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3381" data-mp="1vidzhiux8eos">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-3382" data-mp="1lm19ow2hykzb" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-3383" data-mp="1cu90spt4fjwu">{errors.firstName}</p>}
            </div>
            <div data-id="element-3384" data-mp="xwygcqyi33oz">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3385" data-mp="xwgp68qp3fnx">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-3386" data-mp="j8gbre9aaecp" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-3387" data-mp="t8769fxugtcs">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-3388" data-mp="o6q6xov0deyb">
            <div data-id="element-3389" data-mp="127uasalvefa2">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3390" data-mp="127aokhp6t4q8">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-3391" data-mp="1bxvw4umu9jla" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-3392" data-mp="1lvjt7r7x336m">{errors.email}</p>}
            </div>
            <div data-id="element-3393" data-mp="1gx8ile9ssonc">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3394" data-mp="1gxrqs39nbyo6">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-3395" data-mp="2dckm26mu20uv" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-3396" data-mp="24auk46vbrjiu">{errors.phone}</p>}
            </div>
          </div>

          {/* Consent stays directly under the phone field. While the search
              that supplies the disclosure is in flight, the wait is stated in
              the visitor's terms rather than as a technical status. */}
          {matchFlow && searchLoading ? <div className="flex items-center gap-2.5 pt-1" role="status" aria-live="polite" data-id="element-3397" data-mp="1lleex68p32w3">
              <LoaderIcon className="w-4 h-4 animate-spin text-[#00BFA5] flex-shrink-0" aria-hidden="true" data-id="element-3398" data-mp="1gmblkj22ul3y" />
              <p className="text-sm font-semibold text-[#003B5C]" data-id="element-3399" data-mp="1bni9mv2fv25w">
                Finding school matches
              </p>
            </div> : <>
              {matchFlow && tcpaFailed && <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-id="element-3400" data-mp="3ncy5k6pprj4f">
                  <p className="text-xs font-medium text-red-800" data-id="element-3401" data-mp="3s7u0pfq8ymlr">
                    We couldn't load the consent details.
                  </p>
                  <button type="button" onClick={runTcpaSearch} className="mt-2 inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100 transition-colors duration-200" data-id="element-3402" data-mp="55h4max8tqve">
                    Retry
                  </button>
                </div>}
              <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-5 w-5 rounded border-gray-300 text-[#00BFA5] focus:ring-[#00BFA5] cursor-pointer flex-shrink-0" data-id="element-3403" data-mp="3dkiln3uuwkgw">
                {matchFlow ? tcpa?.disclosure ? <span dangerouslySetInnerHTML={{
              __html: sanitizeTcpaHtml(tcpa.disclosure)
            }} data-id="element-3404" data-mp="2pu4urgo0crnw" /> : DEFAULT_TCPA_DISCLOSURE : UMA_TCPA_DISCLOSURE}
              </TCPADisclosure>
            </>}

          {submitError && <p className="text-xs text-red-600" data-id="element-3405" data-mp="3o6qy5an36t42">{submitError}</p>}

          <div className="flex gap-3 pt-2" data-id="element-3406" data-mp="6phf1a86mjtc">
            <button type="button" onClick={goBack} disabled={isSubmitting} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 transition-colors" data-id="element-3407" data-mp="6slux4v0f3zu">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3408" data-mp="359g7ufjfna0r" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-3409" data-mp="3j8pcchya0fow">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-3410" data-mp="11zcwuvulu4sx" />}
              {isSubmitting ? matchFlow ? 'Finding School Matches...' : 'Submitting...' : matchFlow ? 'See My Matches' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-3411" data-mp="x09r196svujw">
      {/* A. Custom Header — omitted where the site's own nav is the only
          branding the page should carry. */}
      {branded && <header className="bg-white py-2 sm:py-4 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-3412" data-mp="1poxgzi2hv83s">
        <img src="https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp" alt="Ultimate Medical Academy Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-3413" data-mp="1b0uam7kjhueg" />
      </header>}

      {/* B. Hero Section */}
      <section className="bg-gradient-to-r from-[#176B9C] to-[#003B5C] relative overflow-hidden" data-id="element-3414" data-mp="23o6n1owzklu1">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-3415" data-mp="28ggl5s898s7e" />
        {/* Explicit grid placement rather than duplicated copy: on mobile the
            form follows the headline so it lands within reach, and the
            supporting paragraph moves below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-3416" data-mp="13hbjqn1rqd1n">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-3417" data-mp="tr9vnv9f0umq">
            <p className="text-[#4ECDC4] font-medium mb-3 text-lg tracking-wide" data-id="element-3418" data-mp="osfd8ufd1835">
              {branded ? 'Ultimate Medical Academy Online' : 'Online Healthcare Programs'}
              <sup className="text-xs" data-id="element-3419" data-mp="1hgkzxmpz575m">1</sup>
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white tracking-tight" data-id="element-3420" data-mp="1pq9xslhksxvq">
              {umaPrograms.find(p => p.slug === selectedProgram)?.shortTitle || <>
                  Healthcare
                  <br data-id="element-3421" data-mp="1yqotmes61vdh" />
                  Career Training
                </>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-3422" data-mp="23rcnr9boysbx">
            <form onSubmit={e => e.preventDefault()} id="uma-lead-form" data-id="element-3423" data-mp="2hhg11aslfbp8">
              {/* Hidden inputs for tracking */}
              {/* defaultValue, NOT value: the TrustedForm and Jornaya scripts
                  write into these inputs directly. A controlled empty value
                  would reset them on every re-render, and this form re-renders
                  on each step change. */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" defaultValue="" data-id="element-3424" data-mp="26qkw7kf9wx64" />
              <input type="hidden" name="leadid_token" id="leadid_token" defaultValue="" data-id="element-3425" data-mp="21rqdeihb38m0" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#003B5C] mb-4" data-id="element-3426" data-mp="2gge70j7ld89b">
                Learn how we can help you succeed.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-3427" data-mp="2bntd7hooqv5s">
                <div className="bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-3428" data-mp="2pj5keok12e3l"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-3429" data-mp="2ugf921zlelqd">
                {matchFlow && isSubmitting ? <motion.div key="finding" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.18
              }} className="py-10 text-center" role="status" aria-live="polite" data-id="element-3430" data-mp="2ueb4rixs7kpk">
                    <LoaderIcon className="w-8 h-8 mx-auto mb-4 text-[#176B9C] animate-spin" aria-hidden="true" data-id="element-3431" data-mp="3gp2p2vkainr4" />
                    <p className="text-base font-bold text-[#003B5C]" data-id="element-3432" data-mp="31yn4nizp7ju0">
                      Finding School Matches
                    </p>
                    <p className="mt-1.5 text-sm text-gray-500" data-id="element-3433" data-mp="36womk0le1d3w">
                      Checking accredited schools against your answers.
                    </p>
                  </motion.div> : <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-3434" data-mp="2x1d69dubzxu1">
                  {renderFormContent()}
                </motion.div>}
              </AnimatePresence>
            </form>
          </div>

          {/* Supporting copy. After the form in the markup so mobile gets it
              below; the grid puts it back under the headline on desktop. */}
          <p className="text-base lg:text-lg text-white/85 max-w-lg mx-auto lg:mx-0 text-center lg:text-left leading-relaxed lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-3435" data-mp="317kzlj6deg75">
            {branded ? 'UMA students get personalized attention throughout the entire education journey. Our students get support from the very beginning, throughout career training, and after graduation.' : 'Answer a few questions and we will show you the accredited online healthcare programs available in your area, along with the support each one offers.'}
          </p>
        </div>
      </section>

      {/* C. Benefits Icons Row */}
      <section className="bg-[#F4F9FB] py-12 border-b border-gray-200" data-id="element-3436" data-mp="28j5o36mg6g0k">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3437" data-mp="bnrfcrc1uufh">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center" data-id="element-3438" data-mp="6p6agelurmkp">
            {[{
            icon: BookOpenIcon,
            title: 'One-on-one\nAcademic Support'
          }, {
            icon: AwardIcon,
            title: 'Certification Prep²',
            sup: '2'
          }, {
            icon: MonitorIcon,
            title: '24/7 Technical\nSupport'
          }, {
            icon: ClipboardCheckIcon,
            title: "Apply Degree Credits\nToward a Bachelor's³",
            sup: '3'
          }, {
            icon: BriefcaseIcon,
            title: 'Job Search\nAssistance'
          }, {
            icon: LaptopIcon,
            title: 'Laptops for those\nwho qualify⁴',
            sup: '4'
          }].map((item, idx) => <div key={idx} className="flex flex-col items-center" data-id="element-3439" data-mp="99vzuczbsjk1" data-mpi={idx}>
                <item.icon className="w-10 h-10 text-[#176B9C] mb-3 stroke-[1.5]" data-id="element-3440" data-mp="9b73mcsrtrb5" />
                <p className="text-xs font-bold text-[#176B9C] whitespace-pre-line leading-tight" data-id="element-3441" data-mp="1gmnzx1g0b0b3">
                  {item.title.replace(/²|³|⁴/g, '')}
                  {item.sup && <sup className="text-[10px]" data-id="element-3442" data-mp="1glpn50z34qlb">{item.sup}</sup>}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* D. Three Feature Cards */}
      <section className="py-16 bg-white" data-id="element-3443" data-mp="1qjdk7xk5ya6n">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3444" data-mp="1qiklnjvcyqzl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-3445" data-mp="1ukp9g1z4kteb">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/dFP2Bkreu2BvfkWxRv6Va3/Image_1.webp',
            title: 'Financial Aid',
            desc: 'Let us help you explore options for financial aid. We will help you through the process to see if you qualify.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/9FrwRXe1BYLuSp6GRYweVA/image_2.webp',
            title: 'Job Search Assistance',
            desc: 'Our Career Service team helps graduates pursue job opportunities, schedule interviews, and more.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/qcGrjYfSLHXUMihbDJMyJz/image_3.webp',
            title: 'Engaging Online Learning',
            desc: 'Get individual instructor feedback, personalized advisor support, and interact with peers on discussion posts.'
          }].map((card, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-3446" data-mp="2mmg4fhgcaee" data-mpi={idx}>
                <img src={card.img} alt={card.title} className="w-full h-48 object-cover" data-id="element-3447" data-mp="3ttqkt3q4jkjn" />
                <div className="p-6 flex-1" data-id="element-3448" data-mp="uc96zp9xoxbe">
                  <h3 className="text-xl font-bold text-[#003B5C] mb-3" data-id="element-3449" data-mp="qbfiouskxghz">
                    {card.title}
                  </h3>
                  <p className="text-sm text-[#176B9C] font-medium leading-relaxed" data-id="element-3450" data-mp="ljfc4ji07pcf">
                    {card.desc}
                  </p>
                </div>
              </div>)}
          </div>
          <div className="text-center" data-id="element-3451" data-mp="1e104rmk14tvh">
            <button onClick={scrollToForm} className="inline-block bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] text-white font-bold py-3 px-12 rounded-full hover:shadow-lg transition-all" data-id="element-3452" data-mp="1e2uncu7tw9we">
              Take the Next Step
            </button>
          </div>
        </div>
      </section>

      {/* E. '4 out of 5' Stat Section — a UMA-specific employer survey. */}
      {branded && <section className="bg-[#F4F9FB] py-16" data-id="element-3453" data-mp="1jd6qsot9wqrq">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-center gap-12" data-id="element-3454" data-mp="1nfjw5c43ymnz">
          <div className="text-center md:text-right max-w-xs" data-id="element-3455" data-mp="19fiwfzzkxla3">
            <div className="text-[#00BFA5] font-bold text-5xl sm:text-6xl mb-2" data-id="element-3456" data-mp="1eevk2my9ywdq">
              4 <span className="text-[#176B9C]" data-id="element-3457" data-mp="1ecsl3df59swv">out of 5</span>
            </div>
            <p className="text-[#176B9C] text-sm font-medium" data-id="element-3458" data-mp="10gourv7e64po">
              surveyed employers say they are likely to hire a UMA graduate
              again.⁵
            </p>
          </div>
          <div className="flex gap-2 sm:gap-4" data-id="element-3459" data-mp="2uug9380o1crg">
            {[1, 2, 3, 4].map(i => <div key={i} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-[#D8E8F0] flex items-center justify-center bg-white" data-id="element-3460" data-mp="2yv086h4suqg8">
                <CheckCircle2Icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#00BFA5]" data-id="element-3461" data-mp="n5aj5916nm7s" />
              </div>)}
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-[#D8E8F0] flex items-center justify-center bg-transparent" data-id="element-3462" data-mp="j3yqeun0ikvd">
              <CheckCircle2Icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#D8E8F0]" data-id="element-3463" data-mp="s67y7cqwc052" />
            </div>
          </div>
        </div>
      </section>}

      {/* F. 25% Georgetown Study Section */}
      <section className="py-16 bg-white" data-id="element-3464" data-mp="11x2msnrm7bto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3465" data-mp="11vzzsqxextj1">
          <div className="bg-[#003B5C] rounded-2xl overflow-hidden relative flex flex-col md:flex-row items-center shadow-xl" data-id="element-3466" data-mp="1fuzkxolpvqag">
            <div className="p-10 md:p-16 flex-1 z-10 flex items-center gap-8" data-id="element-3467" data-mp="1axr17jkx7e73">
              <div className="text-6xl sm:text-8xl font-bold text-white" data-id="element-3468" data-mp="3ho6z2uplg1ur">
                25<span className="text-4xl sm:text-6xl" data-id="element-3469" data-mp="3cp44y5b5jg1k">%</span>
              </div>
              <div className="w-1 h-24 bg-[#00BFA5] hidden sm:block" data-id="element-3470" data-mp="3f39ag78x7y2p"></div>
              <p className="text-white text-lg sm:text-xl font-medium max-w-sm leading-snug" data-id="element-3471" data-mp="3f5bzop291n6d">
                A Georgetown study found that an associate degree can increase
                lifetime earnings over a high school diploma by 25 percent.
                <sup className="text-base" data-id="element-3472" data-mp="3t1ph5y6s9oku">{branded ? 6 : 5}</sup>
              </p>
            </div>
            <div className="w-full md:w-1/2 h-64 md:h-full absolute right-0 top-0 opacity-80 md:opacity-100" data-id="element-3473" data-mp="2vg1l5ee8p89u">
              <img src="https://cdn.magicpatterns.com/uploads/xs9nMQM6BrsxQ4Td97ACQ9/image.png" alt="Graduate smiling" className="w-full h-full object-cover object-right" data-id="element-3474" data-mp="30ev0zdh01bd6" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#003B5C] to-transparent" data-id="element-3475" data-mp="3a4efy81lcl2r"></div>
            </div>
          </div>
        </div>
      </section>

      {/* G. Testimonial Section — a named UMA graduate. */}
      {branded && <section className="bg-[#176B9C] py-16 relative" data-id="element-3476" data-mp="2hm0vrb96gxm5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3477" data-mp="2a4m7oxfuut1p">
          <div className="text-[#00BFA5] text-6xl font-serif leading-none mb-4" data-id="element-3478" data-mp="254zu2mw47asd">
            "
          </div>
          <p className="text-white text-xl sm:text-2xl font-medium leading-relaxed mb-8" data-id="element-3479" data-mp="205mv53ryyd9f">
            "I'm a working mom and mother of four. I am thankful to UMA for the
            flexibility, so I am able to complete my courses and pass with
            honors!"
          </p>
          <div className="flex items-center justify-center gap-4" data-id="element-3480" data-mp="1r71ifp96hsgk">
            <div className="text-right" data-id="element-3481" data-mp="1mcx9x1k9lcdd">
              <div className="text-white font-bold text-lg" data-id="element-3482" data-mp="1hfezf6qlmp39">Merelesita R.</div>
              <div className="text-white/80 text-sm" data-id="element-3483" data-mp="1hdaxuvg6a265">UMA Graduate</div>
            </div>
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/20" data-id="element-3484" data-mp="1ci59lpzasbew">
              <img src="https://cdn.magicpatterns.com/uploads/xs9nMQM6BrsxQ4Td97ACQ9/image.png" alt="Merelesita R." className="w-full h-full object-cover" data-id="element-3485" data-mp="17gfykac2rxqt" />
            </div>
          </div>
        </div>
        {/* Triangle pointer */}
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-t-[16px] border-t-[#176B9C]" data-id="element-3486" data-mp="xtlrwhl5sk84"></div>
      </section>}

      {/* H. Accreditation Bar */}
      <section className="bg-[#F8F9FA] py-12 mt-4" data-id="element-3487" data-mp="otgun09oj27o">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center gap-6" data-id="element-3488" data-mp="1cirqc3c6a6ey">
          <div className="w-20 h-20 flex-shrink-0 bg-white rounded-full p-2 shadow-sm flex items-center justify-center border border-gray-100" data-id="element-3489" data-mp="1gkmqv5ma4fro">
            <AwardIcon className="w-10 h-10 text-[#003B5C]" data-id="element-3490" data-mp="12sg95f3ha0q4" />
          </div>
          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3491" data-mp="17qi2csumbgfb">
            {branded ? 'UMA is institutionally accredited by the Accrediting Bureau of Health Education Schools (ABHES.org), a national accrediting agency that is recognized by the U.S. Secretary of Education. Ultimate Medical Academy does not recruit or enroll residents of the European Union.' : 'We only feature programs from institutionally accredited schools. Accreditation is reviewed by an independent agency recognized by the U.S. Secretary of Education, and you can verify any school through the U.S. Department of Education or CHEA databases before you enroll.'}
          </p>
        </div>
      </section>

      {/* I. Citations Section */}
      <section className="bg-[#F8F9FA] py-8 border-t border-gray-200" data-id="element-3492" data-mp="1qojsotd8bsmq">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3493" data-mp="1mnhvei2vhqne">
          <h4 className="text-xs font-bold text-gray-800 mb-3" data-id="element-3494" data-mp="20m7o7lint45y">Citations</h4>
          <ol className="text-[10px] text-gray-500 space-y-1 list-decimal list-inside" data-id="element-3495" data-mp="20k4ryjqwyess">
            <li data-id="element-3496" data-mp="1vlti490mc4ak">
              Coursework is online except for on-site externship/professional
              practice experience. Set schedules required for on-site
              externship/professional practice experience courses in select
              programs and coursework deadlines are set by instructors.
            </li>
            <li data-id="element-3497" data-mp="19ephl3tr69aj">
              Learners must meet eligibility criteria to sit for applicable
              certification exams
            </li>
            <li data-id="element-3498" data-mp="1ed0op6sny5p2">
              Tuition considerations and credit transfer decisions are at the
              discretion of the receiving institution.
            </li>
            <li data-id="element-3499" data-mp="1o453a5effojd">
              For full information on laptop program eligibility, please see the
              {branded ? ' UMA Catalog.' : " school's catalog."}
            </li>
            {branded && <li data-id="element-3500" data-mp="1x4jrpdmotjr3">
              Source: Survey was conducted by Hanover Research. Employers that
              hired UMA Online students between Jul 1, 2022 - September 30,
              2023. Survey responses received from 257 employers. Excludes
              corporate alliance partners.
            </li>}
            <li data-id="element-3501" data-mp="7ou14z1saz74">
              Georgetown University. The College Payoff. Study found that an
              associate degree can increase lifetime earnings over a high school
              diploma by 25 percent.
            </li>
          </ol>
          <p className="text-[10px] text-gray-500 mt-4" data-id="element-3502" data-mp="3rixob88hiuv1">
            Where national data regarding earnings or employability within a
            particular field are cited, individual readers should take note that
            national averages may not accurately reflect the earnings of workers
            in their particular part of the country, and may include earners at
            all stages of their career, not just entry-level wages for recent
            graduates.
          </p>
          <p className="text-[10px] text-gray-500 mt-2" data-id="element-3503" data-mp="957z088ov0j1">
            Set schedules required for on-site externship / professional
            practice experience courses in select programs and in all programs,
            coursework deadlines are set by instructors.
          </p>
        </div>
      </section>

      {/* J. UMA Footer — the site's own footer covers the unbranded page. */}
      {branded && <footer className="bg-white py-12 border-t border-gray-200" data-id="element-3504" data-mp="1on2dt3fl99fm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center md:items-start gap-8" data-id="element-3505" data-mp="1jo7l99rtk6ib">
          <img src="https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp" alt="Ultimate Medical Academy Logo" className="h-14 object-contain" data-id="element-3506" data-mp="1yd42nxfrmvvl" />
          <div className="text-center md:text-left text-xs text-gray-600" data-id="element-3507" data-mp="27chbcnw2fgf9">
            <p className="mb-1" data-id="element-3508" data-mp="23bfbq5y8l2cr">
              © 2026 UMA Education, Inc. d/b/a Ultimate Medical Academy. All
              rights reserved.
            </p>
            <p className="mb-4" data-id="element-3509" data-mp="2haoh0akwmc1y">
              A member of the Vocate Education Solutions System
            </p>
            <LegalLinks className="flex justify-center md:justify-start" data-id="element-3510" data-mp="2ccwn5b0p8fv4" />
          </div>
        </div>
      </footer>}
    </div>;
}